import { Box, Button } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import Typography from "@mui/material/Typography";
import clsx from "clsx";
import Image from "next/image";
import { ReactNode, useEffect, VFC } from "react";
import { ContactSupportButton } from "../components/ContactSupportButton";
import { useCaptureError } from "../hooks/useCaptureError";

const useStyles = makeStyles(
  (theme) => ({
    root: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      alignItems: "center",
      justifyContent: "center",
    },
    errorText: {
      margin: theme.spacing(4, "auto", 0),
    },
    heading: {
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing(),
    },
    content: {
      maxWidth: 720,
      padding: theme.spacing(0, 2),
      position: "relative",
      textAlign: "center",
      width: "100%",
    },
    supportLink: {
      marginTop: theme.spacing(2),
    },
    goBtn: {
      marginTop: theme.spacing(3),
    },
  }),
  { classNamePrefix: "Status" }
);

export type StatusProps = {
  className?: string;
  code: number;
  heading?: string;
  message?: ReactNode;
  hideGoHome?: boolean;
  hideSupportLink?: boolean;
} & (
  | {
      captureStatus?: false;
    }
  | {
      captureStatus: true;
      associatedError?: Error;
    }
);

export const Status: VFC<StatusProps> = ({
  code,
  heading = "Oops, something went wrong.",
  message = "This page doesn't exist. Try going back to our homepage.",
  hideGoHome,
  hideSupportLink,
  className,
  captureStatus,
  associatedError,
}: StatusProps & { associatedError?: Error }) => {
  const classes = useStyles();

  const { captureError } = useCaptureError();

  useEffect(() => {
    if (captureStatus)
      captureError(associatedError !== undefined ? associatedError : "Status component captured a status", {
        statusCode: code,
      });
  }, [associatedError, captureError, captureStatus, code]);

  return (
    <div className={clsx(classes.root, className)}>
      <main className={classes.content}>
        {(() => {
          switch (code) {
            case 404:
              return <Image src="/img/404.svg" layout="responsive" width="480" height="173" />;
            case 5000:
              return <Image src="/img/5000.svg" layout="responsive" width="595" height="173" />;
          }
        })()}

        <Box className={classes.errorText}>
          <Typography variant="h1" className={classes.heading}>
            {heading}
          </Typography>
          <Typography>{message}</Typography>
          {!hideGoHome && (
            <Button className={classes.goBtn} color="primary" variant="contained" href="/">
              Go back home
            </Button>
          )}
        </Box>
        {!hideSupportLink && (
          <div className={classes.supportLink}>
            <ContactSupportButton />
          </div>
        )}
      </main>
    </div>
  );
};

export default Status;
